import { useMemo } from 'react';
import { AnalyticsUtils, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { BarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useFetchAggregatedLongDurationCallReports } from '../../hooks/use-fetch-aggregated-long-duration-call-reports';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { ChartProps } from './shared-types';

const colors = {
  incoming: theme.colors.secondary.seaweed30,
  outgoing: theme.colors.warning50,
};

const labels = {
  incoming: i18next.t('Incoming', { ns: 'analytics-phone' }),
  outgoing: i18next.t('Outgoing', { ns: 'analytics-phone' }),
};

type ProcessedData = {
  chartData: BarChartData;
  totals: {
    incoming: number;
    outgoing: number;
  };
};

export const LongDurationCallsChart = ({ defaultChartAppearance = {}, exportPdfProps, isSmallMode }: ChartProps) => {
  const { t } = useTranslation('analytics-phone');
  const { accessibleLocationsNames, filterHintText, isDemoAccount } = usePhoneAnalyticsShallowStore(
    'accessibleLocationsNames',
    'filterHintText',
    'isDemoAccount'
  );
  const { data, isHourlyData, isLoading, isMultiLocation } = useFetchAggregatedLongDurationCallReports();

  const processedData: ProcessedData = useMemo(() => {
    let groups: BarChartData['groups'] = [];
    const totals: ProcessedData['totals'] = {
      incoming: 0,
      outgoing: 0,
    };

    if (isMultiLocation) {
      // There will be multiple location objects with single/multiple dates
      groups = Object.entries(data?.aggregatedMultiLocations || {}).map(([key, { inbound, outbound }]) => {
        totals.incoming += inbound || 0;
        totals.outgoing += outbound || 0;

        return {
          name: accessibleLocationsNames?.[key] || key,
          values: {
            incoming: inbound || 0,
            outgoing: outbound || 0,
          },
        };
      });
    } else {
      // For hourly data: There will be only one object with single date and hourly data for the given location
      // For daily data: There will be only one object with multiple dates for the given location
      const locationData = Object.values(data?.locations || {})[0] || {};

      if (isHourlyData) {
        // Hourly data is restricted to a single date, hence only one object
        const { inbound, outbound } = Object.values(locationData || {})[0] || {};
        const incomingHourly = inbound?.hourly || [];
        const outgoingHourly = outbound?.hourly || [];

        groups = incomingHourly.map((incoming, i) => {
          totals.incoming += incoming;
          totals.outgoing += outgoingHourly[i];

          return {
            name: AnalyticsUtils.getHourlyInsightsLabel(i),
            values: {
              incoming,
              outgoing: outgoingHourly[i],
            },
          };
        });
      } else {
        groups = Object.entries(locationData).map(([date, { inbound, outbound }]) => {
          totals.incoming += inbound?.total || 0;
          totals.outgoing += outbound?.total || 0;

          return {
            name: formatters.date(date),
            values: {
              incoming: inbound?.total || 0,
              outgoing: outbound?.total || 0,
            },
          };
        });
      }
    }

    return {
      chartData: {
        groups,
      },
      totals,
    };
  }, [accessibleLocationsNames, data, isHourlyData, isMultiLocation]);

  return (
    <Chart colors={colors} isLoading={isLoading} labels={labels}>
      <Chart.Header
        {...exportPdfProps}
        infoTip={<InfoTipPopover>{t('Number of calls with duration greater than 10 mins')}</InfoTipPopover>}
        leftElement={isDemoAccount && <DemoChip />}
        subtitle={!isSmallMode && filterHintText}
        title={t('Long Duration Calls')}
      />
      <Chart.BarChart
        appearance={{
          ...defaultChartAppearance,
          customYAxisTickFormat: undefined,
        }}
        data={processedData.chartData}
      />
      <Chart.Legends formatValue={formatters.value} values={processedData.totals} />
    </Chart>
  );
};
